import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type {
  FeatureFlagClientConfiguration,
  FeatureFlagClientConfigurationMap,
  FeatureFlagDrawerMode,
  FeatureFlagFormFieldErrors,
  FeatureFlagMetadataFormValues,
  FeatureFlagTableRow,
  FeatureFlagTargetClient,
} from '../../../types/features/featureFlags';
import { createEmptyFeatureClientConfigurations } from '../constants/featureFlagOptions';
import {
  buildCreateFeatureFlagRequest,
  buildReplaceFeatureFlagStoresRequest,
  buildReplaceFeatureFlagUsersRequest,
  buildUpdateFeatureFlagRequest,
  shouldReplaceStoresAfterFeatureUpdate,
  shouldReplaceUsersAfterFeatureUpdate,
} from '../mappers/featureFlagPayloadMapper';
import { mapFeatureFlagResponseToTableRow } from '../mappers/featureFlagTableRowMapper';
import featureFlagAdminService from '../services/featureFlagAdminService';
import showToast from '../../../utils/toastUtils';
import { validateFeatureFlagForm } from '../validators/featureFlagFormSchema';
import {
  CREATE_ASSIGNMENTS_ERROR_MESSAGE,
  EMPTY_FEATURE_METADATA_VALUES,
  getSubmitErrorMessage,
  UPDATE_ASSIGNMENTS_ERROR_MESSAGE,
} from '../utils/featureFlagsPageControlUtils';
import { useFeatureFlagCreateAssignmentSaver } from './useFeatureFlagCreateAssignmentSaver';

type UseFeatureFlagSubmitHandlersArgs = {
  activeClientConfiguration: FeatureFlagClientConfiguration;
  clientConfigurations: FeatureFlagClientConfigurationMap;
  clientFilter: FeatureFlagTargetClient;
  drawerMode: FeatureFlagDrawerMode;
  editingFeatureRow: FeatureFlagTableRow | null;
  featureMetadataValues: FeatureFlagMetadataFormValues;
  handleRefreshFeatureRows: () => Promise<void>;
  handleResetStoreFilters: () => void;
  selectedTargetClient: FeatureFlagTargetClient | null;
  setActiveTargetClient: Dispatch<SetStateAction<FeatureFlagTargetClient>>;
  setClientConfigurations: Dispatch<SetStateAction<FeatureFlagClientConfigurationMap>>;
  setClientFilter: (client: FeatureFlagTargetClient) => void;
  setDrawerMode: Dispatch<SetStateAction<FeatureFlagDrawerMode>>;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setEditingFeatureRow: Dispatch<SetStateAction<FeatureFlagTableRow | null>>;
  setFeatureFieldErrors: Dispatch<SetStateAction<FeatureFlagFormFieldErrors>>;
  setFeatureFormError: Dispatch<SetStateAction<string>>;
  setFeatureMetadataValues: Dispatch<SetStateAction<FeatureFlagMetadataFormValues>>;
  setFeatureSubmitting: Dispatch<SetStateAction<boolean>>;
  setStoreAssignmentsChanged: Dispatch<SetStateAction<boolean>>;
  setUserAssignmentsChanged: Dispatch<SetStateAction<boolean>>;
  storeAssignmentsChanged: boolean;
  userAssignmentsChanged: boolean;
};

export const useFeatureFlagSubmitHandlers = ({
  activeClientConfiguration,
  clientConfigurations,
  clientFilter,
  drawerMode,
  editingFeatureRow,
  featureMetadataValues,
  handleRefreshFeatureRows,
  handleResetStoreFilters,
  selectedTargetClient,
  setActiveTargetClient,
  setClientConfigurations,
  setClientFilter,
  setDrawerMode,
  setDrawerOpen,
  setEditingFeatureRow,
  setFeatureFieldErrors,
  setFeatureFormError,
  setFeatureMetadataValues,
  setFeatureSubmitting,
  setStoreAssignmentsChanged,
  setUserAssignmentsChanged,
  storeAssignmentsChanged,
  userAssignmentsChanged,
}: UseFeatureFlagSubmitHandlersArgs) => {
  const saveFeatureAssignmentsAfterCreate = useFeatureFlagCreateAssignmentSaver();

  const handleCreateFeature = useCallback(async () => {
    const selectedClientConfiguration = selectedTargetClient
      ? clientConfigurations[selectedTargetClient]
      : activeClientConfiguration;
    const validationErrors = await validateFeatureFlagForm({
      ...featureMetadataValues,
      assignmentTarget: selectedClientConfiguration.assignmentTarget,
      globalEnabled: selectedClientConfiguration.globalEnabled,
      selectedTargetClient,
      storeIds: selectedClientConfiguration.storeIds,
      userIds: selectedClientConfiguration.userIds,
    }, 'create');

    if (Object.keys(validationErrors).length > 0) {
      setFeatureFieldErrors(validationErrors);
      setFeatureFormError('');
      return;
    }

    if (!selectedTargetClient) {
      return;
    }

    setFeatureFieldErrors({});
    setFeatureFormError('');
    setFeatureSubmitting(true);

    try {
      const createdFeature = await featureFlagAdminService.createFeatureFlag(
        buildCreateFeatureFlagRequest(featureMetadataValues, selectedTargetClient, selectedClientConfiguration),
      );

      try {
        await saveFeatureAssignmentsAfterCreate(createdFeature.id, selectedClientConfiguration);
      } catch {
        setEditingFeatureRow(mapFeatureFlagResponseToTableRow(createdFeature));
        setDrawerMode('update');
        setFeatureFormError(CREATE_ASSIGNMENTS_ERROR_MESSAGE);

        if (selectedTargetClient === clientFilter) {
          await handleRefreshFeatureRows();
        } else {
          setClientFilter(selectedTargetClient);
        }
        return;
      }

      showToast.success('Feature flag created successfully.');
      if (selectedTargetClient === clientFilter) {
        await handleRefreshFeatureRows();
      } else {
        setClientFilter(selectedTargetClient);
      }
      setActiveTargetClient('webapp');
      setClientConfigurations(createEmptyFeatureClientConfigurations());
      setDrawerOpen(false);
      setFeatureMetadataValues(EMPTY_FEATURE_METADATA_VALUES);
      handleResetStoreFilters();
    } catch (error) {
      setFeatureFormError(getSubmitErrorMessage(error, 'Failed to create feature flag.'));
    } finally {
      setFeatureSubmitting(false);
    }
  }, [
    activeClientConfiguration,
    clientConfigurations,
    clientFilter,
    featureMetadataValues,
    handleRefreshFeatureRows,
    handleResetStoreFilters,
    saveFeatureAssignmentsAfterCreate,
    selectedTargetClient,
    setActiveTargetClient,
    setClientConfigurations,
    setClientFilter,
    setDrawerMode,
    setDrawerOpen,
    setEditingFeatureRow,
    setFeatureFieldErrors,
    setFeatureFormError,
    setFeatureMetadataValues,
    setFeatureSubmitting,
  ]);

  const handleUpdateFeature = useCallback(async () => {
    if (!editingFeatureRow) {
      setFeatureFormError('Select a feature flag to update.');
      return;
    }

    const selectedClientConfiguration = clientConfigurations[editingFeatureRow.client];

    const validationErrors = await validateFeatureFlagForm({
      ...featureMetadataValues,
      assignmentTarget: selectedClientConfiguration.assignmentTarget,
      globalEnabled: selectedClientConfiguration.globalEnabled,
      selectedTargetClient: editingFeatureRow.client,
      storeIds: selectedClientConfiguration.storeIds,
      userIds: selectedClientConfiguration.userIds,
    }, 'update');

    if (Object.keys(validationErrors).length > 0) {
      setFeatureFieldErrors(validationErrors);
      setFeatureFormError('');
      return;
    }

    setFeatureFieldErrors({});
    setFeatureFormError('');
    setFeatureSubmitting(true);

    try {
      await featureFlagAdminService.updateFeatureFlag(
        editingFeatureRow.id,
        buildUpdateFeatureFlagRequest(featureMetadataValues, selectedClientConfiguration),
      );

      const shouldReplaceStoreAssignments = shouldReplaceStoresAfterFeatureUpdate(
        editingFeatureRow.globalEnabled,
        editingFeatureRow.rolloutTarget,
        selectedClientConfiguration,
        storeAssignmentsChanged,
      );
      const shouldReplaceUserAssignments = shouldReplaceUsersAfterFeatureUpdate(
        editingFeatureRow.globalEnabled,
        editingFeatureRow.rolloutTarget,
        selectedClientConfiguration,
        userAssignmentsChanged,
      );

      if (shouldReplaceStoreAssignments) {
        try {
          await featureFlagAdminService.replaceFeatureFlagStores(
            editingFeatureRow.id,
            buildReplaceFeatureFlagStoresRequest(selectedClientConfiguration.storeIds),
          );
        } catch {
          setFeatureFormError(UPDATE_ASSIGNMENTS_ERROR_MESSAGE);
          await handleRefreshFeatureRows();
          return;
        }
      }

      if (shouldReplaceUserAssignments) {
        try {
          await featureFlagAdminService.replaceFeatureFlagUsers(
            editingFeatureRow.id,
            buildReplaceFeatureFlagUsersRequest(selectedClientConfiguration.userIds),
          );
        } catch {
          setFeatureFormError(UPDATE_ASSIGNMENTS_ERROR_MESSAGE);
          await handleRefreshFeatureRows();
          return;
        }
      }

      showToast.success('Feature flag updated successfully.');
      await handleRefreshFeatureRows();
      setDrawerOpen(false);
      setEditingFeatureRow(null);
      setFeatureMetadataValues(EMPTY_FEATURE_METADATA_VALUES);
      handleResetStoreFilters();
      setStoreAssignmentsChanged(false);
      setUserAssignmentsChanged(false);
    } catch (error) {
      setFeatureFormError(getSubmitErrorMessage(error, 'Failed to update feature flag.'));
    } finally {
      setFeatureSubmitting(false);
    }
  }, [
    clientConfigurations,
    editingFeatureRow,
    featureMetadataValues,
    handleRefreshFeatureRows,
    handleResetStoreFilters,
    setDrawerOpen,
    setEditingFeatureRow,
    setFeatureFieldErrors,
    setFeatureFormError,
    setFeatureMetadataValues,
    setFeatureSubmitting,
    setStoreAssignmentsChanged,
    setUserAssignmentsChanged,
    storeAssignmentsChanged,
    userAssignmentsChanged,
  ]);

  const handleSaveFeature = useCallback(async () => {
    if (drawerMode === 'update') {
      await handleUpdateFeature();
      return;
    }

    await handleCreateFeature();
  }, [drawerMode, handleCreateFeature, handleUpdateFeature]);

  return {
    handleCreateFeature,
    handleSaveFeature,
    handleUpdateFeature,
  };
};
